import React, { useState, useEffect, useContext } from "react";
import Main from "../../components/Main";
import TabelaDinamica from "../../components/TabelaDinamica";
import MensagemSucesso from "../../components/MensagemSucesso";
import MensagemErro from "../../components/MensagemErro";
import styles from "./styles.module.css";
import CalculateIcon from "@mui/icons-material/Calculate";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Grid,
  InputAdornment,
} from "@mui/material";
import CRUDPageHeader from "../../components/CRUDPageHeader";
import PaperComponent from "../../components/PaperComponent";
import {
  obterBasesCalculo,
  obterBasesCalculoPorCampoDeBusca,
  cadastrarBasesCalculo,
  atualizarBasesCalculo,
  excluirBasesCalculo,
} from "../../services/api-esocial.service";
import { handleErrorResponse } from "../../utils/error.util";
import { Context } from "src/config/AuthContext";
import BasesCalculoForm from "src/forms/BasesCalculoForm";
import { formatToBRL } from "src/utils/currency.util";
import { BRLCurrencyFormat } from "src/components/BRLCurrencyFormat";
import GenericTextField from "src/components/GenericTextField";
import GenericNumberField from "src/components/GenericNumberField";

const Calculo20S = () => {
  const [valorDiferencaBase, setValorDiferencaBase] = useState(32420);
  const [taxa20s, setTaxa20s] = useState(Number(5.8));

  const { handleLogout } = useContext(Context);

  const [loading, setLoading] = useState(false);
  const [editRowData, setEditRowData] = useState({});
  const [msg, setMsg] = useState("");

  const [idGrupoEconomico, setIdGrupoEconomico] = useState(null);
  const [idEmpresa, setIdEmpresa] = useState(null);
  const [basesCalculoData, setBasesCalculoData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openDialogExclusao, setOpenDialogExclusao] = useState(false);
  const [openSnackbarErro, setOpenSnackbarErro] = useState(false);
  const [openSnackbarSucesso, setOpenSnackbarSucesso] = useState(false);

  const [exportFilename, setExportFilename] = useState("");
  const [exportData, setExportData] = useState([]);

  const columns = [
    { id: "idBaseCalculo", label: "ID", field: "idBaseCalculo" },
    { id: "referencia", label: "Referência", field: "referencia_cellval" },
    {
      id: "valor",
      label: "Base de Cálculo",
      field: "valor_cellval",
      type: "monetario",
    },
    {
      id: "diferencaBase",
      label: "Diferença Base",
      field: "diferencaBase_cellVal",
      type: "monetario",
    },
    {
      id: "calculo20s",
      label: "Cálculo 20S",
      field: "calculo20s_cellVal",
      type: "monetario",
    },
    {
      id: "taxaSelic",
      label: "Taxa Selic",
      field: "taxaSelic_cellVal",
      type: "porcentagem",
    },
    {
      id: "acrescimo_selic",
      label: "Acréscimo Selic",
      field: "acrescimo_selic_cellVal",
      type: "monetario",
    },
    {
      id: "valor_atualizado",
      label: "Valor Atualizado",
      field: "valor_atualizado_cellVal",
      type: "monetario",
    },
  ];

  const handleOpenModal = async (rowData) => {
    rowData.idBaseCalculo !== undefined
      ? setEditRowData(rowData)
      : setEditRowData({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSaveChanges = async (data) => {
    setLoading(true);

    if (!editRowData.idBaseCalculo) {
      await cadastrarBasesCalculo(idEmpresa, { baseCalculo: data.baseCalculo })
        .then(async (response) => {
          setMsg(response.mensagem);
          await getBasesCalculo(idEmpresa);
          handleCloseModal();
          handleOpenSnackbarSucesso();
        })
        .catch((err) =>
          handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
        );
    } else {
      await atualizarBasesCalculo(idEmpresa, editRowData.idBaseCalculo, {
        baseCalculo: data.baseCalculo,
      })
        .then(async (response) => {
          setMsg(response.mensagem);
          await getBasesCalculo(idEmpresa);
          handleCloseModal();
          handleOpenSnackbarSucesso();
        })
        .catch((err) =>
          handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
        );
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    await excluirBasesCalculo(idEmpresa, editRowData.idBaseCalculo)
      .then(async (response) => {
        setMsg(response.mensagem);
        await getBasesCalculo(idEmpresa);
        handleCloseDialogExclusao();
        handleOpenSnackbarSucesso();
      })
      .catch((err) =>
        handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout)
      );
    setLoading(false);
  };

  const getBasesCalculo = async (idEmpresa) => {
    try {
      const response = await obterBasesCalculo(idEmpresa);
      setupData(response.basesCalculo);
    } catch (err) {
      handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout);
    }
  };

  const setupData = (basesCalculo) => {
    setBasesCalculoData(addTableCellValues(basesCalculo));
    setExportData(setupExportData(basesCalculo));
    setExportFilename(getExportFilename(idGrupoEconomico, idEmpresa));
  };

  const addTableCellValues = (basesCalculo) => {
    basesCalculo.forEach((base) => {
      base.referencia_cellval =
        base.referencia.split("-")[1] + "/" + base.referencia.split("-")[0];

      base.valor_cellval = base.valor;

      base.diferencaBase =
        parseFloat(base.valor - valorDiferencaBase) > 0
          ? parseFloat(base.valor - valorDiferencaBase)
          : 0;

      base.diferencaBase_cellVal = base.diferencaBase;

      base.calculo20s =
        parseFloat((base.diferencaBase * taxa20s) / 100) > 0
          ? parseFloat((base.diferencaBase * taxa20s) / 100)
          : 0;

      base.calculo20s_cellVal = base.calculo20s;

      base.taxaSelic_cellVal = base.taxaSelic ? base.taxaSelic : "0.00";

      base.acrescimo_selic =
        base.calculo20s * base.taxaSelic > 0
          ? ((parseFloat(base.calculo20s) * parseFloat(base.taxaSelic)) / 100)
          : 0;

      base.acrescimo_selic_cellVal = !base.taxaSelic
        ? ""
        : base.acrescimo_selic
        ? base.acrescimo_selic
        : "0.00";

      base.valor_atualizado =
        base.calculo20s + base.acrescimo_selic > 0
          ? parseFloat(base.calculo20s + base.acrescimo_selic)
          : 0;

      base.valor_atualizado_cellVal = !base.taxaSelic
        ? ""
        : base.valor_atualizado
        ? base.valor_atualizado
        : "0.00";
    });
    return basesCalculo;
  };

  const setupExportData = (basesCalculo) => {
    var exportData = [];
    var exportDataSums = {
      "<style bold>Referência</style>": "<style bold>Total</style>",
      "<style bold>Base de Cálculo</style>": Number(0),
      "<style bold>Diferença Base</style>": Number(0),
      "<style bold>Cálculo 20S</style>": Number(0),
      "<style bold>Taxa Selic</style>": "",
      "<style bold>Acréscimo Selic</style>": Number(0),
      "<style bold>Valor Atualizado</style>": Number(0),
    };
    basesCalculo.forEach((base) => {
      exportData.push({
        "<style bold>Referência</style>": `<style numFmt=date:mm/yyyy>${
          base.referencia + "-01"
        }</style>`,
        "<style bold>Base de Cálculo</style>": `<style numFmt=number:0.00>${base.valor}</style>`,
        "<style bold>Diferença Base</style>": `<style numFmt=number:0.00>${base.diferencaBase}</style>`,
        "<style bold>Cálculo 20S</style>": `<style numFmt=number:0.00>${base.calculo20s}</style>`,
        "<style bold>Taxa Selic</style>": `<style numFmt=number:0.00>${base.taxaSelic}</style>`,
        "<style bold>Acréscimo Selic</style>": `<style numFmt=number:0.00>${base.acrescimo_selic}</style>`,
        "<style bold>Valor Atualizado</style>": `<style numFmt=number:0.00>${base.valor_atualizado}</style>`,
      });
      exportDataSums["<style bold>Base de Cálculo</style>"] = (
        parseFloat(exportDataSums["<style bold>Base de Cálculo</style>"]) +
        parseFloat(base.valor)
      ).toFixed(2);
      exportDataSums["<style bold>Diferença Base</style>"] = (
        parseFloat(exportDataSums["<style bold>Diferença Base</style>"]) +
        parseFloat(base.diferencaBase)
      ).toFixed(2);
      exportDataSums["<style bold>Cálculo 20S</style>"] = (
        parseFloat(exportDataSums["<style bold>Cálculo 20S</style>"]) +
        parseFloat(base.calculo20s)
      ).toFixed(2);

      exportDataSums["<style bold>Acréscimo Selic</style>"] = (
        parseFloat(exportDataSums["<style bold>Acréscimo Selic</style>"]) +
        parseFloat(base.acrescimo_selic)
      ).toFixed(2);
      exportDataSums["<style bold>Valor Atualizado</style>"] = (
        parseFloat(exportDataSums["<style bold>Valor Atualizado</style>"]) +
        parseFloat(base.valor_atualizado)
      ).toFixed(2);
    });
    exportData.push({
      "<style bold>Referência</style>": "<style bold>Valores Base</style>",
      "<style bold>Base de Cálculo</style>": "",
      "<style bold>Diferença Base</style>": valorDiferencaBase,
      "<style bold>Cálculo 20S</style>": taxa20s,
      "<style bold>Taxa Selic</style>": "",
      "<style bold>Acréscimo Selic</style>": "",
      "<style bold>Valor Atualizado</style>": "",
    });
    exportData.push({
      "<style bold>Referência</style>":
        exportDataSums["<style bold>Referência</style>"],
      "<style bold>Base de Cálculo</style>":
        `<style numFmt=number:0.00>${
          exportDataSums["<style bold>Base de Cálculo</style>"]
        }</style>`,
      "<style bold>Diferença Base</style>":
        `<style numFmt=number:0.00>${
          exportDataSums["<style bold>Diferença Base</style>"]
        }</style>`,
      "<style bold>Cálculo 20S</style>":
        `<style numFmt=number:0.00>${
          exportDataSums["<style bold>Cálculo 20S</style>"]
        }</style>`,
      "<style bold>Taxa Selic</style>":
          exportDataSums["<style bold>Taxa Selic</style>"],
      "<style bold>Acréscimo Selic</style>":
        `<style numFmt=number:0.00>${
          exportDataSums["<style bold>Acréscimo Selic</style>"]
        }</style>`,
      "<style bold>Valor Atualizado</style>":
        `<style numFmt=number:0.00>${
          exportDataSums["<style bold>Valor Atualizado</style>"]
        }</style>`,
    });
    return exportData;
  };

  const getExportFilename = (idGrupoEconomico, idEmpresa) => {
    return "export_"
      .concat(idGrupoEconomico)
      .concat("_")
      .concat(idEmpresa)
      .concat("_")
      .concat(
        new Date()
          .toISOString()
          .replace(/\.\d{3}/, "")
          .replace("T", " ")
          .replace("Z", "")
          .replaceAll(":", "-")
          .replace(" ", "_")
      );
  };

  const handleOpenSnackbarSucesso = () => {
    setOpenSnackbarSucesso(true);
  };

  const handleCloseSnackbarSucesso = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbarSucesso(false);
  };

  const handleOpenSnackbarErro = () => {
    setOpenSnackbarErro(true);
  };

  const handleCloseSnackbarErro = (event, reason) => {
    if (!reason === "clickaway") {
      return;
    }
    setOpenSnackbarErro(false);
  };

  const handleClickOpenDialogExclusao = (rowData) => {
    setEditRowData(rowData);
    setOpenDialogExclusao(true);
  };

  const handleCloseDialogExclusao = () => {
    setOpenDialogExclusao(false);
  };

  const handleDebouncerChange = async (event) => {
    setLoading(true);
    try {
      const response = await obterBasesCalculoPorCampoDeBusca(
        idEmpresa,
        event.target.value
      );
      const basesCalculo = addTableCellValues(response.basesCalculo);
      setBasesCalculoData(basesCalculo);
      setExportData(setupExportData(basesCalculo));
      setExportFilename(getExportFilename(idGrupoEconomico, idEmpresa));
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setBasesCalculoData([]);
      } else {
        handleErrorResponse(err, setMsg, handleOpenSnackbarErro, handleLogout);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    setupData(basesCalculoData);
  }, [valorDiferencaBase, taxa20s]);

  useEffect(() => {
    const fetchData = async () => {
      if (idGrupoEconomico !== null && idEmpresa !== null) {
        setLoading(true);
        await getBasesCalculo(idEmpresa);
        setLoading(false);
      } else {
        setLoading(true);
        setBasesCalculoData([]);
        setExportData([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [idEmpresa]);

  const handleGrupoECnpjChange = async (ids) => {
    setIdGrupoEconomico(ids.GE);
    setIdEmpresa(ids.CNPJ);
  };

  return (
    <div>
      <Main />

      <CRUDPageHeader
        headerIcon={<CalculateIcon fontSize="30px" />}
        headerTitle={"Cálculo 20S"}
        handleDebounceChange={handleDebouncerChange}
        handleOpenModal={handleOpenModal}
        modalButtonLabel={"Novo"}
        AutoCompleteGrupoECnpj
        handleGrupoECnpjChange={handleGrupoECnpjChange}
      />

      <div className={styles.backgroundContainer}>
        <Grid container spacing={2} direction={"row-reverse"}>
          <Grid item xs={2}>
            <GenericNumberField
              label="Taxa 20S"
              value={taxa20s}
              onChange={(value) => {
                setTaxa20s(value);
              }}
              decimals={5}
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <GenericTextField
              label="Valor Diferença Base"
              value={valorDiferencaBase}
              onChange={(event) => setValorDiferencaBase(event.target.value)}
              variant="standard"
              InputProps={{
                inputComponent: BRLCurrencyFormat,
              }}
            />
          </Grid>
        </Grid>
      </div>

      <TabelaDinamica
        data={basesCalculoData}
        columns={columns}
        loading={loading}
        orderColumn={"idUsuario"}
        onEditButtonClick={handleOpenModal}
        onDeleteButtonClick={handleClickOpenDialogExclusao}
        exportFilename={exportFilename}
        exportData={exportData}
      />

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        PaperProps={{
          style: {
            width: "140%", // Defina a largura desejada
          },
        }}
        maxWidth="lg" // Defina o tamanho máximo do modal
      >
        <DialogTitle className={styles.popupHeader}>
          <div className={styles.linhaTituloPopup} />
          <div className={styles.tituloPopupContainer}>
            <CalculateIcon className={styles.iconeTituloPopup} />
            <h1 className={styles.tituloPopup}>
              {editRowData.idUsuario
                ? "Edição de Base de Cálculo"
                : "Cadastro de Base de Cálculo"}
            </h1>
          </div>
        </DialogTitle>
        <BasesCalculoForm
          basesCalculoValues={Object.assign({}, editRowData)}
          onSubmit={handleSaveChanges}
          onClose={handleCloseModal}
          className={styles.popupContent}
        ></BasesCalculoForm>
      </Dialog>

      {/* Dialog de exclusão */}
      <Dialog
        open={openDialogExclusao}
        onClose={handleCloseDialogExclusao}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          className={styles.tituloExclusao}
          style={{ cursor: "move" }}
          id="draggable-dialog-title"
        >
          Atenção!
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={styles.textoExclusao}>
            Você realmente deseja excluir permanentemente a base de cálculo{" "}
            {editRowData.referencia_cellval} - {editRowData.valor_cellval}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles.botaoPopupCancelarExclusao}
            autoFocus
            onClick={handleCloseDialogExclusao}
          >
            Cancelar
          </Button>
          <Button className={styles.botaoPopupExcluir} onClick={handleDelete}>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <MensagemSucesso
        open={openSnackbarSucesso}
        handleClose={handleCloseSnackbarSucesso}
        msg={msg}
      />

      <MensagemErro
        open={openSnackbarErro}
        handleClose={handleCloseSnackbarErro}
        msg={msg}
      />
    </div>
  );
};

export default Calculo20S;
